import { useEffect } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";

import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SelectXnuupPartIdAutoCompleteComponent from "@/main/components/shared/xnuupPartIdAutoComplete/selectXnuupPartIdAutoCompleteComponent";

import { KeyValue } from "@/main/models/common";
import { PositionRequest } from "@/main/models/request";
import { SubcategoryRequest } from "@/main/models/request/subcategoryRequest";
import { SupplierInventoryDataResponse, XnuupPartIdAutoCompleteResponse } from "@/main/models/response";
import { StatusSupplierInventoryWarehouseEnum } from "@/main/services/enum/StatusSupplierInventoryWarehouseEnum";
import {
	createKeyValue,
	createKeyValueId,
	createOptionalKeyValue,
	createOptionalKeyValueId,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
} from "@/main/services/utils";
import { getCategory, resetReducerCategory, useCategoryReducer } from "@/main/store/category";
import { getPosition, resetReducerPosition, usePositionReducer } from "@/main/store/position";
import { getSubcategory, resetReducerSubcategory, useSubcategoryReducer } from "@/main/store/subcategory";

import { IInventoryRegisterForm } from "../../../utilsInventoryRegisterScreen";
import * as S from "./styles";

interface IAreaFormMappingPartProps {
	dataInventory: SupplierInventoryDataResponse | null;
	searchText: string;
}
function AreaFormMappingPart({ searchText, dataInventory }: IAreaFormMappingPartProps) {
	const dispatch = useDispatch();
	const { category: categoryReducer } = useCategoryReducer();
	const { subcategory: subcategoryReducer } = useSubcategoryReducer();
	const { position: positionReducer } = usePositionReducer();
	const optionDefaultCategoryDrop = getDefaultOptionDrop(categoryReducer.data);
	const optionDefaultSubcategoryDrop = getDefaultOptionDrop(subcategoryReducer.data);
	const optionDefaultPositionDrop = getDefaultOptionDrop(positionReducer.data);

	const { control, setValue, register, resetField, clearErrors } = useFormContext<IInventoryRegisterForm>();
	const { errors } = useFormState({
		control,
	});
	const watcherCategory = useWatch({
		name: "category",
		control,
	});
	const watcherSubcategory = useWatch({
		name: "subCategory",
		control,
	});
	const watcherXnuupPartId = useWatch({
		name: "xnuupPartId",
		control,
	});
	const watcherPosition = useWatch({
		name: "position",
		control,
	});

	const hasOneValue =
		!isNullUndefined(watcherCategory) ||
		!isNullUndefined(watcherSubcategory) ||
		!isNullUndefined(watcherXnuupPartId) ||
		!isNullUndefined(watcherPosition);
	const showAreaForm = searchText === "" && hasOneValue;

	//#region Functions Call Domains
	function callSubcategoryApiDomain(optionSelected: KeyValue | null) {
		setValue("subCategory", null);
		setValue("position", null);
		setValue("xnuupPartId", null);

		dispatch(resetReducerSubcategory());
		dispatch(resetReducerPosition());

		if (optionSelected !== null && optionSelected.key) {
			const request: SubcategoryRequest = {
				guid: String(optionSelected.key),
				isCanastaBasica: true,
			};
			dispatch(getSubcategory(request));
		}
	}
	function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
		if (!partId) return resetField("xnuupPartId");

		const {
			partCategoryGuid,
			partCategoryName,
			partGuid,
			partPIESName,
			partSubCategoryGuid,
			partSubCategoryName,
			piesPartTerminologyId,
		} = partId;
		if (partCategoryGuid && partCategoryName) {
			const valueCategory = createKeyValue(partCategoryGuid, partCategoryName);
			clearErrors();
			setValue("category", valueCategory);
			if (isNullUndefined(categoryReducer.data)) dispatch(getCategory());

			if (partSubCategoryGuid && partSubCategoryName) {
				const valueSubCategory = createKeyValue(partSubCategoryGuid, partSubCategoryName);
				setValue("subCategory", valueSubCategory);

				const request: SubcategoryRequest = {
					guid: String(partCategoryGuid),
					isCanastaBasica: true,
				};
				dispatch(getSubcategory(request));

				if (partGuid) {
					const request: PositionRequest = {
						partGuid: String(partGuid),
					};
					dispatch(getPosition(request));
				} else {
					dispatch(resetReducerPosition());
					setValue("position", null);
				}
			} else {
				dispatch(resetReducerSubcategory());
				setValue("subCategory", null);
			}
		} else {
			dispatch(resetReducerCategory());
			setValue("category", null);
		}
		const valueXnuupPartId = createKeyValueId(partGuid, partPIESName, piesPartTerminologyId);
		setValue("xnuupPartId", valueXnuupPartId);
	}
	//#endregion

	function loadDataInventory() {
		if (isNullUndefined(dataInventory)) return;

		const valueCategory = createOptionalKeyValue(dataInventory.guidPartCategory, dataInventory.category);
		const valueSubCategory = createOptionalKeyValue(dataInventory.guidSubcategory, dataInventory.subcategory);
		const valuePosition = createOptionalKeyValue(dataInventory.guidPosition, dataInventory.position);
		const valueXnuupPartId = createOptionalKeyValueId(
			dataInventory.partGuid,
			dataInventory.partPIESName,
			dataInventory.xnuupId,
		);

		if (!isNullUndefined(valueCategory)) {
			callSubcategoryApiDomain(valueCategory);
		}
		if (!isNullUndefined(valueXnuupPartId)) {
			const request: PositionRequest = {
				partGuid: String(dataInventory.partGuid),
			};
			dispatch(getPosition(request));
		}

		setValue("category", valueCategory);
		setValue("subCategory", valueSubCategory);
		setValue("position", valuePosition);
		setValue("xnuupPartId", valueXnuupPartId);
	}

	useEffect(() => {
		if (isNullUndefined(categoryReducer.data) && !showAreaForm) dispatch(getCategory());
	}, []);

	useEffect(() => {
		loadDataInventory();
	}, [JSON.stringify(dataInventory)]);

	const isPendingToMap =
		!isNullUndefined(dataInventory) && dataInventory.idStatus === StatusSupplierInventoryWarehouseEnum.PendingToMap;

	const isDisabledSubcategoryDrop = () => isNullUndefined(subcategoryReducer.data) || isNullUndefined(watcherCategory);
	const isDisabledPositionDrop = () => isNullUndefined(positionReducer.data) || isNullUndefined(watcherXnuupPartId);

	if (!showAreaForm) return null;
	return (
		<S.RowInput pendingToMap={isPendingToMap}>
			<KogAutoCompleteControlled
				id="id-inventoryRegister-txt-category"
				nameCustom="category"
				label={getTranslateByKey("quoteCard.category")}
				control={control}
				rulesCustom={{ required: true }}
				errorMessageCustom={
					errors.category?.type === "required" ? getTranslateByKey("information.categoryRequired") : ""
				}
				hasErrorCustom={!!errors.category}
				options={categoryReducer.data}
				isLoading={categoryReducer.isLoading}
				onChangeCustom={(optionSelected) => {
					callSubcategoryApiDomain(optionSelected);
				}}
				autoHighlightCustom={!!optionDefaultCategoryDrop}
				setValueCustom={setValue}
			/>
			<KogAutoCompleteControlled
				id="id-inventoryRegister-txt-subcategory"
				nameCustom="subCategory"
				label={getTranslateByKey("quoteCard.subCategory")}
				control={control}
				rulesCustom={{ required: true }}
				options={subcategoryReducer.data}
				isLoading={subcategoryReducer.isLoading}
				onChangeCustom={() => {}}
				disabled={isDisabledSubcategoryDrop() || !!optionDefaultSubcategoryDrop}
				isDefaultValueUnique
				setValueCustom={setValue}
			/>

			<SelectXnuupPartIdAutoCompleteComponent
				watcherCategory={watcherCategory}
				watcherSubcategory={watcherSubcategory}
				partPIESName={watcherXnuupPartId?.value}
				handleChangeXnuupPartId={(partId) => {
					loadOptionsByXnuupPartId(partId);
				}}
				errorMessageCustom={
					errors.xnuupPartId?.type === "required" ? getTranslateByKey("information.xnuupPartIdRequired", true) : ""
				}
				hasErrorCustom={!!errors.xnuupPartId}
				label={getTranslateByKey("information.xnuupPartId")}
				{...register("xnuupPartId", { required: true })}
			/>

			<KogAutoCompleteControlled
				id="id-inventoryRegister-txt-position"
				nameCustom="position"
				label={getTranslateByKey("information.position")}
				control={control}
				rulesCustom={{ required: true }}
				errorMessageCustom={
					errors.position?.type === "required" ? getTranslateByKey("information.positionRequired") : ""
				}
				hasErrorCustom={!!errors.position}
				options={positionReducer.data}
				isLoading={positionReducer.isLoading}
				onChangeCustom={() => {}}
				disabled={isDisabledPositionDrop() || !!optionDefaultPositionDrop}
				autoHighlightCustom={!!optionDefaultPositionDrop}
				setValueCustom={setValue}
			/>
		</S.RowInput>
	);
}

export default AreaFormMappingPart;
